import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { UseBoolean } from 'react-hanger';
import type { OptionSchema } from 'c-components';
import {
    Alert,
    Autocomplete,
    AutoGrid,
    Button,
    ControlledFormInput,
    DialogV2,
    EntityAutocomplete,
    FormWrapper,
    SelectField,
    TextField,
} from 'c-components';
import { Translate, useCommonTranslation } from 'c-translation';
import { useAPIClientRequest, useAuthenticatedUser, useEntityField } from 'c-hooks';
import { apiClient, useEntityData, useUpdateEntityData } from 'c-data';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import type { Client, Buyer, Campaign } from 'c-sdk';
import { PermissionName, UserType } from 'c-sdk';
import { CampaignFormNames } from 'c-main/Types';
import useCampaignTypeOptions from 'c-main/Hooks/useCampaignTypeOptions';
import to from 'await-to-js';
import { NetworkRequestState } from 'c-data-layer';
import { Box, DialogActions, Tab, Tabs } from '@mui/material';
import { useUserPermissions } from 'c-auth-module/Hooks';
import { EditCampaignLineItems } from 'c-main/Components';
import { CampaignUpdateDialogPermissions, UpdateDisplaysPermissions } from './SideBar/constants';
import { campaignDetailsFormDefaults } from './campaignEditDefaults';

type Props = { campaign: Campaign; dialogState: UseBoolean; source: string };

const CampaignOverviewEdit: React.FC<Props> = ({ campaign, dialogState, source }) => {
    const t = useCommonTranslation();
    const { hasAll } = useUserPermissions();
    const canEditDetails = hasAll(CampaignUpdateDialogPermissions);
    const canUpdateCampaign = hasAll(UpdateDisplaysPermissions);
    const { start: loadUpdateCampaign, data: updateCampaignData } = useAPIClientRequest(
        apiClient.Entities.Campaign.get,
    );
    const [activeTab, setActiveTab] = useState<'details' | 'update'>(
        canEditDetails ? 'details' : 'update',
    );
    // const { text: AgencyName } = useEntityField<Agency>(campaign.agency_id, 'Agency', 'name');
    const { text: ClientName } = useEntityField<Client>(campaign.client_id, 'Client', 'name');
    const { text: BuyerName } = useEntityField<Buyer>(campaign.buyer_id, 'Buyer', 'name');
    const { update, getUpdatingById, resetUpdatingById } =
        useUpdateEntityData<Campaign>('Campaign');
    const updating = getUpdatingById(campaign.id);
    const isUpdating = updating.state === NetworkRequestState.InProgress;
    // Whichever tab's form is showing; the other is unmounted and has reported false.
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        if (!dialogState.value) return;
        if (canEditDetails) {
            setActiveTab('details');
            return;
        }
        if (canUpdateCampaign) {
            setActiveTab('update');
        }
    }, [dialogState.value, canEditDetails, canUpdateCampaign]);

    useEffect(() => {
        if (!dialogState.value || activeTab !== 'update' || !canUpdateCampaign) return;

        loadUpdateCampaign(campaign.id, [
            'buyer',
            'budgetSummary',
            'budgetSummary.lineItems',
            'lineItems',
        ]);
    }, [activeTab, campaign.id, canUpdateCampaign, dialogState.value, loadUpdateCampaign]);

    useEffect(() => {
        if (updating.state === NetworkRequestState.Success && dialogState.value) {
            resetUpdatingById(campaign.id);
            dialogState.setFalse();
        }
    }, [updating.state, dialogState, campaign.id, resetUpdatingById]);

    const onSubmitDetails = useCallback(
        (data: Partial<Campaign>) => {
            update(campaign.id, data as Campaign, ['additionalContacts']);
        },
        [campaign.id, update],
    );

    const defaultData = useMemo(() => campaignDetailsFormDefaults(campaign), [campaign]);
    const updateCampaign = useMemo(
        () => (updateCampaignData?.data?.data as Campaign | undefined) ?? campaign,
        [campaign, updateCampaignData?.data?.data],
    );

    if (!canEditDetails && !canUpdateCampaign) return null;

    return (
        <DialogV2
            open={dialogState.value}
            onClose={dialogState.setFalse}
            isDirty={isDirty}
            title="Modules.Main.Campaigns.Overview.EditDialog.title"
            maxWidth="xl"
            titleDivider
        >
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    variant="fullWidth"
                    sx={{
                        minHeight: 68,
                        '& .MuiTabs-indicator': { height: 3 },
                    }}
                >
                    {canEditDetails && (
                        <Tab
                            value="details"
                            label={t(
                                'Modules.Main.Campaigns.Overview.ActionLabels.campaignOverview',
                            )}
                            sx={{
                                minHeight: 68,
                                maxWidth: 'none',
                                px: 3,
                                fontSize: '1.125rem',
                                fontWeight: 700,
                                letterSpacing: '0.02em',
                                textTransform: 'none',
                            }}
                        />
                    )}
                    {canUpdateCampaign && (
                        <Tab
                            value="update"
                            label={t(
                                'Modules.Main.Campaigns.Overview.ActionLabels.campaignDetails',
                            )}
                            sx={{
                                minHeight: 68,
                                maxWidth: 'none',
                                px: 3,
                                fontSize: '1.125rem',
                                fontWeight: 700,
                                letterSpacing: '0.02em',
                                textTransform: 'none',
                            }}
                        />
                    )}
                </Tabs>
            </Box>

            {activeTab === 'details' && canEditDetails && (
                <FormWrapper
                    onSubmit={onSubmitDetails}
                    formOptions={{ defaultValues: defaultData }}
                    onDirtyChange={setIsDirty}
                >
                    <EditCampaignForm
                        campaignId={campaign.id}
                        // agencyName={AgencyName}
                        buyerId={campaign.buyer_id}
                        clientName={ClientName}
                        buyerName={BuyerName}
                    />
                    {updating.error != null && (
                        <Box mt={2}>
                            <Alert severity="error">{String(updating.error)}</Alert>
                        </Box>
                    )}
                    <DialogActions sx={{ px: 0, pt: 3 }}>
                        <Button type="submit" disabled={isUpdating}>
                            {t('CommonWords.Save')}
                        </Button>
                    </DialogActions>
                </FormWrapper>
            )}

            {activeTab === 'update' && canUpdateCampaign && (
                <>
                    {campaign?.flags?.under_configuration === true ? (
                        <Alert severity="warning" variant="outlined">
                            <Translate path="Modules.Main.Campaigns.Overview.underConstructionWarning" />
                        </Alert>
                    ) : (
                        <EditCampaignLineItems
                            campaign={updateCampaign}
                            source={source}
                            redirectOnSuccess={false}
                            onSuccess={dialogState.setFalse}
                            onDirtyChange={setIsDirty}
                        />
                    )}
                </>
            )}
        </DialogV2>
    );
};
// const agencyLabelGen = (entity, t: AppTranslationFunction) =>
//     t('Modules.Main.Campaigns.Overview.EditDialog.agencyDropdownLabel', entity);

// const alterSearchTerm = (term: string) => {
//     if (term == null) {
//         return term;
//     }
//
//     // we currently include the campaign count next to the agency name, e.g. `Sky (200 campaigns)`
//     // want to remove that additional campaign count part from the actual http search
//
//     const insideBrackets = term.match(/\(([^)]+)\)/)?.[1] ?? '';
//     return term.replace(insideBrackets, '').replace('(', '').replace(')', '').trim();
// };

const EditCampaignForm = ({
    campaignId,
    // agencyName,
    clientName,
    buyerName,
    buyerId,
}: {
    campaignId: number;
    buyerId: number;
    // agencyName: string;
    clientName: string;
    buyerName: string;
}) => {
    const t = useCommonTranslation();
    const campaignTypeOptions = useCampaignTypeOptions();
    const { user } = useAuthenticatedUser();
    const hideBuyer = user?.availableUserTypes?.includes(UserType.Owner) ?? false;
    const { start, data } = useAPIClientRequest(apiClient.Entities.Buyer.get);
    const loadSelectedBuyer = useCallback(
        async (buyerId: number) => {
            await to(start(buyerId, ['users', 'latestCost']));
        },
        [start],
    );
    useEffect(() => {
        if (buyerId != null) loadSelectedBuyer(buyerId);
    }, [buyerId, loadSelectedBuyer]);
    const buyerUsers = useMemo(
        () => (buyerId == null ? [] : data?.data?.data?.users ?? []),
        [buyerId, data?.data?.data?.users],
    );
    const buyerUserOptions = useMemo<OptionSchema[]>(
        () => buyerUsers.map(user => ({ label: user.name, value: user.id })),
        [buyerUsers],
    );
    return (
        <AutoGrid spacing={2} xs={12}>
            <ControlledFormInput
                render={({ field }) => (
                    <TextField
                        {...field}
                        label={t('Modules.Main.Campaigns.Overview.EditDialog.campaignNameLabel')}
                    />
                )}
                name="name"
            />
            <ControlledFormInput
                render={({ field }) => (
                    <TextField
                        {...field}
                        label={t('Modules.Main.Campaigns.Overview.EditDialog.referenceLabel')}
                    />
                )}
                name="reference"
            />
            {!hideBuyer && (
                <IfHasAllPermissions permissions={[PermissionName.UniledportalBuyerRead]}>
                    <ControlledFormInput
                        render={({ field }) => (
                            <EntityAutocomplete
                                {...field}
                                multiple={false}
                                entityName="Buyer"
                                textFieldProps={{
                                    label: t(
                                        'Modules.Main.Campaigns.Overview.OverviewSection.buyerHeader',
                                    ),
                                }}
                                searchColumns="name"
                                labelColumn="name"
                                autoLoadSearchTerm={buyerName}
                            />
                        )}
                        name="buyer_id"
                    />
                </IfHasAllPermissions>
            )}
            <IfHasAllPermissions
                permissions={[
                    PermissionName.UniledportalUserRead,
                    PermissionName.UniledportalCampaign_userCreate,
                    PermissionName.UniledportalCampaign_userRead,
                    PermissionName.UniledportalCampaign_userUpdate,
                ]}
            >
                <ControlledFormInput
                    name={CampaignFormNames.additional_contacts}
                    render={({ field }) => (
                        <Autocomplete
                            {...field}
                            id="campaign-edit-additional-contacts"
                            multiple
                            options={buyerUserOptions}
                            textFieldProps={{
                                label: t(
                                    'Modules.Main.Campaigns.Create.additionalContactsDropdownLabel',
                                ),
                            }}
                        />
                    )}
                />
            </IfHasAllPermissions>
            <IfHasAllPermissions permissions={[PermissionName.UniledportalClientRead]}>
                <ControlledFormInput
                    render={({ field }) => (
                        <EntityAutocomplete
                            {...field}
                            multiple={false}
                            entityName="Client"
                            textFieldProps={{
                                label: t(
                                    'Modules.Main.Campaigns.Overview.OverviewSection.brandHeader',
                                ),
                            }}
                            searchColumns="name"
                            labelColumn="name"
                            autoLoadSearchTerm={clientName}
                        />
                    )}
                    name="client_id"
                />
            </IfHasAllPermissions>
            <IfHasAllPermissions permissions={[PermissionName.AdminCampaignEdit_campaign_type]}>
                <ControlledFormInput
                    name="type"
                    render={({ field }) => (
                        <SelectField
                            value={field.value}
                            label={t(
                                'Modules.Main.Campaigns.Overview.OverviewSection.campaignType',
                            )}
                            options={campaignTypeOptions}
                            onChange={field.onChange}
                        />
                    )}
                />
            </IfHasAllPermissions>

            {/* <IfHasAllPermissions permissions={[PermissionName.UniledportalAgencyRead]}> */}
            {/*    <ControlledFormInput */}
            {/*        render={({ field }) => (* /}
            {/*            <EntityAutocomplete */}
            {/*                {...field} */}
            {/*                multiple={false} */}
            {/*                entityName="Agency" */}
            {/*                textFieldProps={{ */}
            {/*                    label: t( */}
            {/*                        'Modules.Main.Campaigns.Overview.OverviewSection.agencyHeader', */}
            {/*                    ), */}
            {/*                }} */}
            {/*                searchColumns="name" */}
            {/*                labelColumn="name" */}
            {/*                includes={['campaignsCount']} */}
            {/*                orderByField="campaignsCount" */}
            {/*                orderByDirection="desc" */}
            {/*                labelGen={agencyLabelGen} */}
            {/*                autoLoadSearchTerm={agencyName} */}
            {/*                alterSearchTerm={alterSearchTerm} */}
            {/*            /> */}
            {/*        )} */}
            {/*        name="agency_id" */}
            {/*    /> */}
            {/* </IfHasAllPermissions> */}
        </AutoGrid>
    );
};

const CampaignOverviewEditWrapper: React.FC<{
    id: number;
    dialogState: UseBoolean;
    source: string;
}> = ({ id, dialogState, source }) => {
    const { getById } = useEntityData<Campaign>('Campaign');
    const campaign = getById({ id });
    return (
        campaign && (
            <CampaignOverviewEdit campaign={campaign} dialogState={dialogState} source={source} />
        )
    );
};

export default CampaignOverviewEditWrapper;
